import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { ApiError, api } from "../api/client";
import { applyLanguage } from "../i18n";
import type { LoginMode, Me } from "../types";

type PermissionKey =
  | "can_view_compensation"
  | "can_edit_compensation"
  | "can_view_documents"
  | "can_manage_training"
  | "can_manage_leave"
  | "can_manage_onboarding"
  | "can_view_reports"
  | "can_manage_payroll"
  | "can_manage_certifications"
  | "can_manage_settings"
  | "can_manage_announcements"
  | "can_manage_expenses"
  | "can_manage_performance"
  | "can_manage_employees"
  | "can_approve_leave"
  | "can_approve_expenses"
  | "can_start_reviews"
  | "can_view_team"
  | "can_manage_roles"
  | "can_view_bank_details"
  | "can_edit_bank_details"
  | "can_view_bonuses"
  | "can_edit_bonuses"
  | "can_manage_deductions";
import {
  primeCameraPermission,
  primeGeolocationPermission,
} from "../utils/permissions";

interface LoginOptions {
  identifier?: string;
  pin: string;
  mode?: LoginMode;
}

interface AuthContextValue {
  employee: Me | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isKiosk: boolean;
  hasPermission: (key: PermissionKey) => boolean;
  login: (opts: LoginOptions) => Promise<Me>;
  onLoggedIn: () => Promise<Me>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  isAdminPinVerified: boolean;
  verifyAdminPin: (pin: string) => Promise<void>;
  setInitialAdminPin: (pin: string) => Promise<void>;
  viewAsLevel: ViewAsLevel | null;
  setViewAsLevel: (level: ViewAsLevel | null) => void;
}

export interface ViewAsLevel {
  name: string;
  is_admin: boolean;
  rank?: number;
  access_level_name?: string;
  /** Set when previewing a specific employee (not an access level), so pages
   *  that support server-side previews (e.g. My Team) can show that
   *  person's data where it is safe to do so. */
  employee_id?: number;
  can_view_compensation: boolean;
  can_edit_compensation: boolean;
  can_view_documents: boolean;
  can_manage_training: boolean;
  can_manage_leave: boolean;
  can_manage_onboarding: boolean;
  can_view_reports: boolean;
  can_manage_payroll: boolean;
  can_manage_certifications: boolean;
  can_manage_settings: boolean;
  can_manage_announcements: boolean;
  can_manage_expenses: boolean;
  can_manage_performance: boolean;
  can_manage_employees: boolean;
  can_approve_leave: boolean;
  can_approve_expenses: boolean;
  can_start_reviews: boolean;
  can_view_team: boolean;
  can_manage_roles: boolean;
  can_view_bank_details: boolean;
  can_edit_bank_details: boolean;
  can_view_bonuses: boolean;
  can_edit_bonuses: boolean;
  can_manage_deductions: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const AUTH_CACHE_KEY = "pme_auth_cache";

function readAuthCache(): Me | null {
  try {
    const raw = sessionStorage.getItem(AUTH_CACHE_KEY);
    return raw ? (JSON.parse(raw) as Me) : null;
  } catch { return null; }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const cached = readAuthCache();
  const [employee, setEmployee] = useState<Me | null>(cached);
  const [loading, setLoading] = useState(!cached);
  const [viewAsLevel, setViewAsLevel] = useState<ViewAsLevel | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<Me>("/api/auth/me");
      setEmployee(me);
      sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(me));
      applyLanguage(me.preferred_language);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setEmployee(null);
        sessionStorage.removeItem(AUTH_CACHE_KEY);
      } else {
        console.error("auth refresh failed", err);
        setEmployee(null);
        sessionStorage.removeItem(AUTH_CACHE_KEY);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const finaliseLogin = useCallback(async () => {
    const me = await api.get<Me>("/api/auth/me");
    setEmployee(me);
    applyLanguage(me.preferred_language);
    if (!me.is_kiosk) {
      void primeCameraPermission();
      primeGeolocationPermission();
    }
    return me;
  }, []);

  const login = useCallback(
    async ({ identifier, pin, mode = "personal" }: LoginOptions) => {
      await api.post("/api/auth/login", { identifier, pin, mode });
      return finaliseLogin();
    },
    [finaliseLogin],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      setEmployee(null);
      sessionStorage.removeItem(AUTH_CACHE_KEY);
      sessionStorage.removeItem("pme_feature_flags");
    }
  }, []);

  const verifyAdminPin = useCallback(
    async (pin: string) => {
      await api.post("/api/auth/verify-admin-pin", { pin });
      await refresh();
    },
    [refresh],
  );

  const setInitialAdminPin = useCallback(
    async (pin: string) => {
      await api.post("/api/auth/set-initial-admin-pin", { pin });
      await refresh();
    },
    [refresh],
  );

  const hasPermissionFn = useCallback(
    (key: PermissionKey): boolean => {
      if (!employee) return false;
      if (viewAsLevel) return !!viewAsLevel[key as keyof ViewAsLevel];
      if (employee.is_super_admin) return true;
      return !!employee[key];
    },
    [employee, viewAsLevel],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      employee,
      loading,
      isAuthenticated: employee !== null,
      isAdmin: viewAsLevel ? !!viewAsLevel.is_admin : !!employee?.is_admin,
      isSuperAdmin: !!employee?.is_super_admin,
      isKiosk: !!employee?.is_kiosk,
      hasPermission: hasPermissionFn,
      isAdminPinVerified: !!employee?.admin_pin_verified,
      login,
      onLoggedIn: finaliseLogin,
      logout,
      refresh,
      verifyAdminPin,
      setInitialAdminPin,
      viewAsLevel,
      setViewAsLevel,
    }),
    [
      employee,
      hasPermissionFn,
      login,
      finaliseLogin,
      logout,
      refresh,
      verifyAdminPin,
      setInitialAdminPin,
      viewAsLevel,
      loading,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}

export function useMyRegion(): {
  regionId: number | null;
  regionName: string | null;
} {
  const { employee } = useAuth();
  return {
    regionId: employee?.region_id ?? null,
    regionName: employee?.region_name ?? null,
  };
}
